// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Forge Rush Slot Machine
// 5-reel, 20-payline video slot with bonus rounds
// ================================================================
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ProvablyFairEngine from '../../engine/ProvablyFairEngine';
import { DEFAULT_SYMBOLS, WILD_SYMBOL, SCATTER_SYMBOL, mapToGrid, buildReelStrip } from '../../engine/ReelMapper';
import { calculateWins, PAYLINES_5x3 } from '../../engine/PaytableCalculator';
import { useCasino } from '../../context/CasinoShell';
import SlotCabinet from '../SlotCabinet';

const RUSH_SYMBOLS = [
  { id: 'forge',    emoji: '🔥', weight: 2,  payout: { 3: 50, 4: 200, 5: 1000 }, name: 'Forge' },
  { id: 'anvil',    emoji: '⚒️',  weight: 3,  payout: { 3: 25, 4: 100, 5: 500 }, name: 'Anvil' },
  { id: 'hammer',   emoji: '🔨', weight: 4,  payout: { 3: 15, 4: 60, 5: 250 }, name: 'Hammer' },
  { id: 'diamond',  emoji: '💎', weight: 5,  payout: { 3: 10, 4: 40, 5: 150 }, name: 'Diamond' },
  { id: 'gold',     emoji: '🥇', weight: 6,  payout: { 3: 8, 4: 30, 5: 100 }, name: 'Gold' },
  { id: 'star',     emoji: '⭐',       weight: 8,  payout: { 3: 5, 4: 20, 5: 60 }, name: 'Star' },
  { id: 'bolt',     emoji: '⚡',       weight: 10, payout: { 3: 3, 4: 10, 5: 30 }, name: 'Bolt' },
  { id: 'cherry',   emoji: '🍒', weight: 12, payout: { 3: 2, 4: 5, 5: 15 }, name: 'Cherry' },
  { ...WILD_SYMBOL, weight: 2 },
  { ...SCATTER_SYMBOL, weight: 2 },
];

const BET_OPTIONS = [0.01, 0.02, 0.05, 0.10, 0.25, 0.50, 1.00, 2.00, 5.00];
const ACTIVE_PAYLINES = PAYLINES_5x3.slice(0, 20);

export default function ForgeRush() {
  const { forgeBalance, placeBet, recordWin, recordLoss, logFairness, isExcluded } = useCasino();
  const engineRef = useRef(new ProvablyFairEngine());

  const [grid, setGrid] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [betPerLine, setBetPerLine] = useState(0.05);
  const [lastWin, setLastWin] = useState(null);
  const [lastProof, setLastProof] = useState(null);
  const [commitmentHash, setCommitmentHash] = useState(null);
  const [message, setMessage] = useState('PLACE YOUR BET');
  const [winAnimation, setWinAnimation] = useState(false);
  const [freeSpins, setFreeSpins] = useState(0);

  const totalBet = betPerLine * ACTIVE_PAYLINES.length;

  useEffect(() => {
    async function init() {
      const commitment = await engineRef.current.createCommitment();
      setCommitmentHash(commitment.commitmentHash);
    }
    init();
    // Init grid
    const reelStrip = buildReelStrip(RUSH_SYMBOLS);
    const initialGrid = [];
    for (let r = 0; r < 5; r++) {
      const reel = [];
      for (let row = 0; row < 3; row++) {
        reel.push(reelStrip[Math.floor(Math.random() * reelStrip.length)]);
      }
      initialGrid.push(reel);
    }
    setGrid(initialGrid);
  }, []);

  const spin = useCallback(async () => {
    if (spinning) return;
    if (isExcluded()) { setMessage('SELF-EXCLUSION ACTIVE'); return; }

    const isFree = freeSpins > 0;
    if (!isFree) {
      const betResult = placeBet(totalBet, 'forge-rush');
      if (!betResult.success) { setMessage(betResult.reason.toUpperCase()); return; }
    } else {
      setFreeSpins(prev => prev - 1);
    }

    setSpinning(true);
    setLastWin(null);
    setWinAnimation(false);
    setMessage(isFree ? `FREE SPIN! (${freeSpins - 1} left)` : 'SPINNING...');

    const clientSeed = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    const result = await engineRef.current.generateResult(clientSeed);
    const proof = engineRef.current.getProof();
    const newGrid = mapToGrid(result.resultValues, 5, 3, RUSH_SYMBOLS);

    // Animate
    const reelStrip = buildReelStrip(RUSH_SYMBOLS);
    for (let frame = 0; frame < 12; frame++) {
      await new Promise(resolve => setTimeout(resolve, 60));
      const tempGrid = [];
      for (let r = 0; r < 5; r++) {
        if (frame >= 7 + r) {
          tempGrid.push(newGrid[r]);
        } else {
          const reel = [];
          for (let row = 0; row < 3; row++) {
            reel.push(reelStrip[Math.floor(Math.random() * reelStrip.length)]);
          }
          tempGrid.push(reel);
        }
      }
      setGrid(tempGrid);
    }
    setGrid(newGrid);

    const winResult = calculateWins(newGrid, betPerLine, ACTIVE_PAYLINES);

    // Check for scatter bonus (free spins)
    if (winResult.scatterWin?.triggersBonus) {
      const bonusSpins = winResult.scatterWin.count === 3 ? 10 : winResult.scatterWin.count === 4 ? 20 : 30;
      setFreeSpins(prev => prev + bonusSpins);
      setMessage(`BONUS! ${bonusSpins} FREE SPINS!`);
    }

    if (winResult.isWin) {
      recordWin(winResult.totalPayout, 'forge-rush', proof.resultHash);
      setLastWin(winResult);
      setWinAnimation(true);
      if (!winResult.scatterWin?.triggersBonus) {
        setMessage(`WIN! $${winResult.totalPayout.toFixed(2)}`);
      }
      setTimeout(() => setWinAnimation(false), 2000);
    } else if (!isFree) {
      recordLoss(totalBet, 'forge-rush', proof.resultHash);
      if (!winResult.scatterWin?.triggersBonus) setMessage('TRY AGAIN');
    }

    logFairness({ gameId: 'forge-rush', ...proof, betAmount: isFree ? 0 : totalBet, winAmount: winResult.totalPayout });
    setLastProof(proof);
    setSpinning(false);
    const nextCommitment = await engineRef.current.createCommitment();
    setCommitmentHash(nextCommitment.commitmentHash);
  }, [spinning, totalBet, betPerLine, placeBet, recordWin, recordLoss, logFairness, isExcluded, freeSpins]);

  // Auto-play free spins
  useEffect(() => {
    if (freeSpins > 0 && !spinning) {
      const timer = setTimeout(spin, 1000);
      return () => clearTimeout(timer);
    }
  }, [freeSpins, spinning, spin]);

  return (
    <SlotCabinet title="FORGE RUSH" icon="⚡" theme="gold"
      stats={[
        { label: 'BALANCE', value: `$${forgeBalance.toFixed(2)}` },
        { label: 'BET', value: `$${totalBet.toFixed(2)}` },
        { label: 'WIN', value: lastWin ? `$${lastWin.totalPayout.toFixed(2)}` : '-' },
        { label: 'FREE', value: freeSpins > 0 ? String(freeSpins) : '-' },
      ]}
    >
      <div style={{ padding: 8 }}>
        <div style={gs.commitBar}>
          <span style={gs.commitLabel}>HASH:</span>
          <span style={gs.commitHash}>{commitmentHash?.slice(0, 16) || '...'}</span>
        </div>

        {/* 5x3 Grid */}
        <div style={gs.reelContainer}>
          {grid?.map((reel, r) => (
            <div key={r} style={gs.reel}>
              {reel.map((sym, row) => (
                <div key={row} style={{
                  ...gs.cell,
                  ...(row === 1 ? gs.cellMiddle : {}),
                  ...(winAnimation && lastWin?.wins?.some(w => w.positions?.some(p => p.reel === r && p.row === row)) ? gs.cellWin : {}),
                }}>
                  <span style={{ fontSize: 28 }}>{sym.emoji}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div style={{ ...gs.message, color: winAnimation ? '#FFD700' : '#FFD700' }}>{message}</div>

        <div style={gs.controls}>
          <div style={gs.betControls}>
            <button style={gs.betBtn} onClick={() => { const i = BET_OPTIONS.indexOf(betPerLine); if (i > 0) setBetPerLine(BET_OPTIONS[i - 1]); }} disabled={spinning}>-</button>
            <span style={gs.betVal}>${betPerLine.toFixed(2)}</span>
            <button style={gs.betBtn} onClick={() => { const i = BET_OPTIONS.indexOf(betPerLine); if (i < BET_OPTIONS.length - 1) setBetPerLine(BET_OPTIONS[i + 1]); }} disabled={spinning}>+</button>
          </div>
          <button style={{ ...gs.spinBtn, opacity: spinning ? 0.5 : 1 }} onClick={spin} disabled={spinning || (forgeBalance < totalBet && freeSpins <= 0)}>
            {spinning ? '⏳' : freeSpins > 0 ? `⚡ FREE (${freeSpins})` : '🎰 SPIN'}
          </button>
        </div>

        {lastProof && (
          <button style={gs.verifyBtn} onClick={async () => {
            const r = await ProvablyFairEngine.verify(lastProof);
            alert(r.valid ? 'VERIFIED FAIR!' : r.reason);
          }}>VERIFY LAST SPIN</button>
        )}
      </div>
    </SlotCabinet>
  );
}

const gs = {
  commitBar: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '4px 6px', background: '#0a0a0a', borderRadius: 4, marginBottom: 6 },
  commitLabel: { fontSize: 7, color: '#555', fontWeight: 700 },
  commitHash: { fontSize: 8, color: '#00FF88', fontFamily: 'monospace' },
  reelContainer: { display: 'flex', justifyContent: 'center', gap: 3, padding: '8px 0', background: 'linear-gradient(180deg, #111 0%, #000 50%, #111 100%)', borderRadius: 6, border: '2px solid #FFD70033' },
  reel: { display: 'flex', flexDirection: 'column', gap: 2 },
  cell: { width: 54, height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a', borderRadius: 4, border: '1px solid #1a1a1a' },
  cellMiddle: { border: '2px solid #FFD70044', background: '#0a0800' },
  cellWin: { border: '2px solid #FFD700', boxShadow: '0 0 10px #FFD70044' },
  message: { textAlign: 'center', fontSize: 12, fontWeight: 900, letterSpacing: 2, padding: '6px 0' },
  controls: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '6px 0' },
  betControls: { display: 'flex', alignItems: 'center', gap: 4 },
  betBtn: { width: 24, height: 24, borderRadius: 4, border: '1px solid #FFD700', background: '#1a1500', color: '#FFD700', fontSize: 14, fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  betVal: { fontSize: 12, color: '#FFD700', fontWeight: 900, minWidth: 45, textAlign: 'center' },
  spinBtn: { padding: '10px 24px', borderRadius: 8, border: '2px solid #FFD700', background: 'linear-gradient(180deg, #FFD700 0%, #B8860B 100%)', color: '#000', fontSize: 13, fontWeight: 900, letterSpacing: 1, cursor: 'pointer' },
  verifyBtn: { display: 'block', width: '100%', marginTop: 6, padding: '6px', borderRadius: 4, border: '1px solid #333', background: 'none', color: '#00FF88', fontSize: 9, fontWeight: 700, cursor: 'pointer', textAlign: 'center' },
};
